import React, { Component, ErrorInfo, ReactNode } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/logo.png";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false, error: null };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-slate-950 px-4 text-center">
          <img src={logo} alt="IntelliWarrior" className="mb-6 h-16 w-16 rounded-full object-contain" />
          <h1 className="text-2xl font-semibold text-white">Something went wrong</h1>
          <p className="mt-2 max-w-md text-sm text-slate-400">
            This part of the app hit an unexpected error. You can try again or return home.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-xl bg-cyan-500 px-5 py-2.5 text-sm font-semibold text-slate-950 hover:brightness-110"
            >
              Reload page
            </button>
            <Link
              to="/"
              className="rounded-xl border border-slate-600 px-5 py-2.5 text-sm font-medium text-slate-200 hover:border-cyan-500/50 hover:text-cyan-200"
            >
              Back to home
            </Link>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
